import { useCallback } from "react";
import { notify as rawNotify, useNotify } from "@/common/notifications";
import { downloadFile, type DownloadFileOptions, type DownloadResult } from "@/lib/downloads/downloadFile";

export type UseDownloadMessages = {
  /** Shown while the download is running, alongside a live percentage. Default: "Downloading" */
  pending?: string;
  /** Shown on success. Default: "Download complete" */
  success?: string;
  /** Shown on failure. Default: "Failed to download" */
  failure?: string;
};

/**
 * Builds the `DownloadFileOptions` for one run, given whatever arguments the caller's trigger
 * function is invoked with (e.g. `includeRules` for a report download). Reports progress as a
 * 0–1 fraction via `onProgress` — the percentage math is the caller's job, since it depends on
 * whatever unit their own data-producing code tracks (nodes, bytes, rows, ...). Return a nullish
 * value to skip the download entirely (e.g. there's nothing to download yet).
 */
export type PrepareDownload<TArgs extends unknown[]> = (
  onProgress: (progress: number) => void,
  ...args: TArgs
) => DownloadFileOptions | null | undefined | Promise<DownloadFileOptions | null | undefined>;

export type UseDownloadResult = DownloadResult | "skipped" | "failed";

const DownloadProgressToast = ({ message, progress }: { message: string; progress: number }) => (
  <span>
    {message}… {Math.round(progress * 100)}%
  </span>
);

/**
 * Wraps `downloadFile` with a progress/success/error toast, so callers only need to describe
 * *what* to download, not how to show that it's happening.
 *
 * @example
 * const downloadReport = useDownload(
 *   (onProgress, includeRules: boolean) => {
 *     if (tree.length === 0) return null;
 *     const report = includeRules ? tree : stripRuleDetailFields(tree);
 *     const totalNodes = countJsonNodes(report);
 *     return {
 *       filename: "report.json",
 *       zip: true,
 *       content: generateJsonChunks(report, 2, undefined, ({ nodesWritten }) =>
 *         onProgress(totalNodes > 0 ? nodesWritten / totalNodes : 1),
 *       ),
 *     };
 *   },
 *   { success: "Report downloaded", failure: "Failed to download report" },
 * );
 * // later: <Button onClick={() => downloadReport(true)} />
 */
export function useDownload<TArgs extends unknown[]>(
  prepare: PrepareDownload<TArgs>,
  messages?: UseDownloadMessages,
): (...args: TArgs) => Promise<UseDownloadResult> {
  const notify = useNotify();
  const pendingMessage = messages?.pending ?? "Downloading";
  const successMessage = messages?.success ?? "Download complete";
  const failureMessage = messages?.failure ?? "Failed to download";

  return useCallback(
    async (...args: TArgs) => {
      let toastId: ReturnType<typeof rawNotify> | undefined;
      let lastUpdateAt = 0;

      const onProgress = (raw: number) => {
        // toastify auto-closes a controlled-progress toast the instant its bar's fill transition
        // reaches 100% (the same mechanism behind toast.done()) — cap just under full so it can't
        // dismiss itself mid-download; the terminal update below replaces it once actually done.
        const progress = Math.min(raw, 0.99);
        const now = Date.now();
        if (toastId === undefined) {
          // A plain manual toast — not .loading()/.promise(), which force the built-in spinner
          // icon on top of our own progress bar. We drive the bar ourselves via `progress`.
          toastId = rawNotify(<DownloadProgressToast message={pendingMessage} progress={progress} />, {
            autoClose: false,
            closeButton: false,
            progress,
          });
          lastUpdateAt = now;
          return;
        }
        // Throttle re-renders — a fast progress source can fire far more often than
        // React/toastify should re-render for.
        if (progress < 0.99 && now - lastUpdateAt < 150) return;
        lastUpdateAt = now;
        notify.update(toastId, {
          render: <DownloadProgressToast message={pendingMessage} progress={progress} />,
          progress,
        });
      };

      const options = await prepare(onProgress, ...args);
      if (!options) return "skipped";

      try {
        const result = await downloadFile(options);

        if (result === "cancelled") {
          // user cancelled the save dialog — not a failure, just clean up the in-progress toast
          if (toastId !== undefined) notify.dismiss(toastId);
          return result;
        }

        // notify.update() merges with the previous toast's props rather than replacing them, so
        // the last progress value would otherwise stick around — explicitly clearing it here
        // (not just omitting it) is what actually falls back to the normal timed autoClose bar.
        // Leaving it set keeps the toast in controlled-progress mode, where autoClose is silently
        // ignored (nothing drives the close timer in that mode).
        if (toastId !== undefined) {
          notify.update(toastId, { render: successMessage, type: "success", progress: undefined, autoClose: 3000 });
        } else {
          notify.success(successMessage);
        }
        return result;
      } catch (error) {
        console.error(failureMessage, error);
        if (toastId !== undefined) {
          notify.update(toastId, { render: failureMessage, type: "error", progress: undefined, autoClose: 5000 });
        } else {
          notify.error(failureMessage);
        }
        return "failed";
      }
    },
    [prepare, notify, pendingMessage, successMessage, failureMessage],
  );
}
